import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import {
  Warning as WarningIcon
} from '@mui/icons-material';

const StatusReasonDialog = ({ 
  open, 
  onClose, 
  onConfirm, 
  status, 
  orderID,
  loading = false 
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
      setError('');
    }
  }, [open]);

  const handleReasonChange = (event) => {
    setReason(event.target.value);
    if (error) setError('');
  };

  const handleConfirm = () => {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    onConfirm(reason.trim());
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getStatusDisplayName = () => {
    switch (status) {
      case 'cancelled':
        return 'Cancelled';
      case 'returned':
        return 'Returned';
      default:
        return status || 'Unknown';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'cancelled':
        return 'error.main';
      case 'returned':
        return 'warning.main';
      default:
        return 'primary.main';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarningIcon sx={{ color: getStatusColor(), fontSize: 32 }} />
          <Box>
            <Typography variant="h6" component="div">
              Confirm Status Change
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Order #{orderID}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Are you sure you want to change the order status to{' '}
          <Box component="span" sx={{ color: getStatusColor(), fontWeight: 'bold' }}>
            {getStatusDisplayName()}
          </Box>
          ?
        </Typography>
        
        <TextField
          label="Reason *"
          placeholder={`Please provide a reason for marking this order as ${getStatusDisplayName().toLowerCase()}...`}
          value={reason}
          onChange={handleReasonChange}
          multiline
          rows={3}
          fullWidth
          error={!!error}
          helperText={error || 'This reason will be saved with the order for future reference'}
          disabled={loading}
          autoFocus
        />
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          color="inherit"
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained" 
          color={status === 'cancelled' ? 'error' : 'warning'}
          disabled={loading || !reason.trim()}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Updating...' : `Mark as ${getStatusDisplayName()}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusReasonDialog;
